import React, { useEffect, useMemo, useState } from "react";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { getCities, deleteCity } from "../services/cityService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import { useAppContext } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";

const CityManagementPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const load = async () => {
    try {
      setLoading(true);
      const res = await getCities();
      setList(Array.isArray(res) ? res : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => {
      const countryName = String(c.country?.countryName || "").toLowerCase();
      const cityName = String(c.cityName || "").toLowerCase();
      const cityCode = String(c.cityCode || "").toLowerCase();
      return (
        countryName.includes(q) || cityName.includes(q) || cityCode.includes(q)
      );
    });
  }, [list, searchTerm]);

  const getRowId = (c) => c?.cityId ?? c?.city_id ?? c?.id ?? null;

  const handleDelete = async (cityId, cityName) => {
    if (cityId == null) return;
    const label = cityName || `ID ${cityId}`;
    const shouldDelete = window.confirm(
      `Are you sure you want to delete "${label}"? This action cannot be undone.`,
    );
    if (!shouldDelete) return;

    try {
      setDeletingId(cityId);
      await deleteCity(cityId);
      setList((prev) => prev.filter((c) => getRowId(c) !== cityId));
      toast.success(`City "${label}" deleted successfully.`);
    } catch (e) {
      toast.error(e?.message || "Failed to delete city");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="page-container city-page">
      {!roleChecked ?
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      : !canAccess ?
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      : <>
          <div className="page-header">
            <div className="header-content">
              <div>
                <h2 className="page-title">City Management</h2>
                <p className="page-subtitle">Manage cities and city codes</p>
              </div>
              <div className="roles-toolbar">
                <div className="roles-search">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Search cities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    className="secondary-btn sm"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </button>
                </div>
                <button
                  className="primary-btn"
                  onClick={() => setActiveSection("city-create")}
                >
                  + Create City
                </button>
              </div>
            </div>
          </div>

          <div className="page-content">
            {loading ?
              <div className="no-results">Loading cities...</div>
            : <div className="kpi-table-container">
                {rows.length === 0 ? (
                  <div className="no-results">No cities found.</div>
                ) : (
                  <table className="kpi-table">
                    <colgroup>
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "48%" }} />
                      <col style={{ width: "24%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "9%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Country</th>
                        <th>City</th>
                        <th>City Code</th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => {
                        const rowId = getRowId(c);
                        return (
                          <tr key={rowId ?? `${c.cityName || "city"}-${c.cityCode || ""}`}>
                            <td>{rowId ?? <span className="empty-text">-</span>}</td>
                            <td>{c.country?.countryName || <span className="empty-text">-</span>}</td>
                            <td>{c.cityName || <span className="empty-text">-</span>}</td>
                            <td>{c.cityCode || <span className="empty-text">-</span>}</td>
                            <td className="actions-cell">
                              <div
                                className="actions-menu-wrapper"
                                style={{ position: "relative", display: "inline-block" }}
                              >
                                <button
                                  className="action-menu-btn"
                                  aria-haspopup="true"
                                  aria-expanded={activeDropdown === rowId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeDropdown === rowId) {
                                      setActiveDropdown(null);
                                    } else {
                                      setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                                      setActiveDropdown(rowId);
                                    }
                                  }}
                                  disabled={rowId == null}
                                >
                                  Actions ▼
                                </button>
                                {activeDropdown === rowId && (
                                  <div
                                    className={`action-dropdown-menu${
                                      dropdownDirection === "up" ? " dropdown-up" : ""
                                    }`}
                                    role="menu"
                                  >
                                    <button
                                      className="action-menu-item"
                                      role="menuitem"
                                      disabled={rowId == null}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(null);
                                        if (rowId == null) return;
                                        try {
                                          localStorage.setItem("activeCityId", String(rowId));
                                        } catch {}
                                        setActiveSection("city-edit", { cityId: rowId });
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="action-menu-item danger"
                                      role="menuitem"
                                      disabled={rowId == null || deletingId === rowId}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(null);
                                        if (rowId != null) {
                                          handleDelete(rowId, c.cityName);
                                        }
                                      }}
                                    >
                                      {deletingId === rowId ? "Deleting..." : "Delete"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            }
          </div>
        </>
      }
    </div>
  );
};

export default CityManagementPage;

